// src/encounter/lib/append-to-assessment-note.ts
//
// Appends ONE line to the A/P narrative, which is held as a PAIR: the plain text
// (`notePlainText`) and the Quill Delta (`notes`) — see `ap-note-dialog.tsx` for why both exist.
// Used by the ambient rule-5 accept (`append-assessment`): a diagnosis the consultation did not
// confirm lands here as text instead of in the diagnosis block.
//
// APPEND ONLY, NEVER REPLACE (ambient I5). Whatever the clinician wrote stays exactly as it was and
// the line goes after it. The one exception is a note with nothing in it — only whitespace, no
// image — which is EMPTY, so the line becomes the whole note. And a line that is ALREADY one of the
// note's lines, exactly, is not added again: each half comes back unchanged, so an accept repeated
// on the same finding writes nothing twice. The two halves are appended separately, each by a
// function of its own previous value, so the host can hand them to two functional state updaters:
// two accepts landing in one tick, or an accept racing a dialog save, then each see the latest text
// rather than a captured one.
//
// It also answers whether a Delta holds content at all (`noteDeltaHasContent`), for the two places
// that must not read an image-only note as empty: the A&P autosave gate and the save mapper.

/**
 * The plain text with `line` added as its own line.
 *
 * A separating newline is added only when the existing text does not already end one, so exactly
 * one line is added either way. The result is newline-terminated, the shape Quill's `getText()`
 * gives the dialog, so a later dialog save and this append agree about where lines end.
 *
 * A note that is only whitespace is EMPTY — '', a lone "\n" (an empty editor's `getText()`), or blank
 * lines — so it gets `line + "\n"` alone. That is the rule `appendLineToNoteDelta` applies to a
 * whitespace-only, image-free Delta, so the halves agree for every image-free pair the host can
 * hold: the dialog saves a whitespace-only note's plain half as '', and a loaded note keeps its blank
 * lines. A note with an IMAGE and no text is the one pair that cannot agree: its Delta is kept whole
 * (never rewritten), while this half, which cannot carry an image, holds the text alone.
 */
export function appendLineToNote(prev: string, line: string): string {
  if (hasLine(prev, line)) return prev;
  const base = isEmptyPlain(prev) ? '' : prev;
  const separator = base === '' || base.endsWith('\n') ? '' : '\n';
  return `${base}${separator}${line}\n`;
}

/** Is `line` already one of the text's lines, EXACTLY? A line inside a longer one does not count. */
function hasLine(text: string, line: string): boolean {
  return text.split('\n').includes(line);
}

/**
 * The same exact-line test, for the host: whether a narrative holds `line` as one of its lines.
 * NOT `text.includes(line)`, which a longer line passes ("Suspected: X — likely" contains
 * "Suspected: X"), so an edited line would count as the line an accept wrote (#539 follow-up).
 */
export function noteHasLine(text: string, line: string): boolean {
  return hasLine(text, line);
}

/** Only whitespace: an empty note. Blank lines carry no clinical content and do not export. */
function isEmptyPlain(text: string): boolean {
  return text.trim() === '';
}

/** The ops of a `notes` value, in either wire shape, or `undefined` when it is not Delta JSON. */
function readOps(delta: string | null | undefined): { ops: unknown[]; envelope: object | null } | undefined {
  if (!delta?.trim()) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(delta);
  } catch {
    return undefined;
  }
  if (Array.isArray(parsed)) return { ops: parsed, envelope: null };
  if (parsed !== null && typeof parsed === 'object' && Array.isArray((parsed as { ops?: unknown }).ops)) {
    return { ops: (parsed as { ops: unknown[] }).ops, envelope: parsed };
  }
  return undefined;
}

/** An op's insert when it is TEXT; `undefined` for an embed (an image) or anything else. */
function textOf(op: unknown): string | undefined {
  const insert = op && typeof op === 'object' ? (op as { insert?: unknown }).insert : undefined;
  return typeof insert === 'string' ? insert : undefined;
}

/**
 * Does the Delta hold anything a clinician put there?
 *
 * Non-whitespace text, or an EMBED — the dialog allows images, and Quill gives an embed no text, so
 * an image-only note saves its plain half as ''. Reading the plain text alone would call that note
 * empty: the autosave would never fire for it and the save would drop it.
 */
export function noteDeltaHasContent(delta: string | null | undefined): boolean {
  const read = readOps(delta);
  if (!read) return false;
  return read.ops.some((op) => {
    const text = textOf(op);
    return text === undefined ? op !== null && typeof op === 'object' && 'insert' in op : text.trim() !== '';
  });
}

/**
 * The Quill Delta (as the `notes` JSON string) with `line` added as its own paragraph, or
 * `undefined` when there is no Delta to append to.
 *
 * `undefined` is not a loss: `toPatientAssessment` derives `notes` from the plain text whenever no
 * Delta is held (`plainTextToDeltaJson`), and the plain text carries the line. The same holds for a
 * `notes` value that is not Delta JSON at all (a legacy plain string), which the dialog also reads
 * as plain text.
 *
 * EVERY existing op of a non-empty document is kept exactly, in order — blank paragraphs and embeds
 * included — and one insert op is pushed after them, so the Delta changes the way the plain text
 * does and the two halves cannot drift apart. A document is non-empty when it holds text or an image. Both wire shapes are read — Flutter's bare ops array
 * and a `{ ops }` envelope — and written back in the shape they came in. A well-formed Quill document already ends
 * in "\n", so the op is `line + "\n"`; a leading "\n" is added only when the last op does not end
 * a line (an embed, or a malformed document), so the line never fuses into the last paragraph.
 * An EMPTY document gets `line + "\n"` alone, exactly what the plain text gets from a whitespace-only
 * note — never a leading blank line. Empty is whitespace-only TEXT and no embed: an empty ops list
 * (`[]`, `{ "ops": [] }`), Quill's own empty document (a lone "\n" op, what the A&P Clear action
 * writes with plain ''), or blank paragraphs alone. The dialog saves such a note's plain half as '',
 * so keeping those ops would give the Delta blank lines the plain half does not have; the plain half
 * is what exports (post-visit summary, HIE), and blank lines carry no clinical content. A document
 * with an image is never empty, whatever its text.
 */
export function appendLineToNoteDelta(prev: string | undefined, line: string): string | undefined {
  const read = readOps(prev);
  if (!read) return undefined;
  // Already one of the document's lines (its text read the way the plain half is): unchanged, the
  // SAME string, so the autosave sees nothing new. Embeds give no text, as in Quill's `getText()`.
  if (hasLine(read.ops.map((op) => textOf(op) ?? '').join(''), line)) return prev;
  const kept = isEmptyDocument(read.ops) ? [] : read.ops;
  const endsLine = kept.length === 0 || textOf(kept[kept.length - 1])?.endsWith('\n') === true;
  const next = [...kept, { insert: endsLine ? `${line}\n` : `\n${line}\n` }];
  return JSON.stringify(read.envelope ? { ...read.envelope, ops: next } : next);
}

/** Whitespace-only text and no embed (the Delta twin of `isEmptyPlain`). `[]` is empty too. */
function isEmptyDocument(ops: unknown[]): boolean {
  return ops.every((op) => textOf(op)?.trim() === '');
}

/**
 * The narrative PAIR with one appended `line` taken back out, or `null` when it is not there
 * VERBATIM in both halves.
 *
 * For Discard (#539): an ambient "Add to assessment" whose line no save had carried yet is settled
 * as NOT written, so the line itself must go too, or the next autosave saves a "Suspected: X" the
 * decision record says was never written. Only that line, and only if it is still exactly as it was
 * appended: an edited line (other words, or formatting in the Delta) is the clinician's now and
 * stays, with both halves untouched.
 *
 * Plain half: the last line equal to `line` is removed. Delta half: the last unformatted text op
 * holding `line` as a whole line loses it (an op left empty is dropped). A `notes` value that is not
 * Delta JSON (absent, or a legacy plain string) is left as it is; the plain half then decides.
 */
export function removeLineFromNotePair(
  plain: string,
  delta: string | undefined,
  line: string,
): { plain: string; delta: string | undefined } | null {
  const lines = plain.split('\n');
  const at = lines.lastIndexOf(line);
  if (at < 0) return null;
  const nextPlain = [...lines.slice(0, at), ...lines.slice(at + 1)].join('\n');

  const read = readOps(delta);
  if (!read) return { plain: nextPlain, delta };
  for (let i = read.ops.length - 1; i >= 0; i -= 1) {
    const op = read.ops[i];
    const text = textOf(op);
    if (text === undefined || (op as { attributes?: unknown }).attributes !== undefined) continue;
    const parts = text.split('\n');
    const j = parts.lastIndexOf(line);
    if (j < 0) continue;
    const rest = [...parts.slice(0, j), ...parts.slice(j + 1)].join('\n');
    const ops = rest === '' ? [...read.ops.slice(0, i), ...read.ops.slice(i + 1)] : read.ops.map((o, k) => (k === i ? { insert: rest } : o));
    const nextDelta = JSON.stringify(read.envelope ? { ...read.envelope, ops } : ops);
    return { plain: nextPlain, delta: nextDelta };
  }
  return null;
}
